/**
 * editFocusStore — one-shot focus signal for newly created items.
 *
 * When a new task/project/list/area is created, the creator calls
 * `requestEditFocus(id)`. The DetailHeader reads and immediately
 * clears the signal via `consumeEditFocus(id)`.
 *
 * Advantages over the old `?editTaskName=true` query string approach:
 * - No URL pollution (cleaner browser history)
 * - No cleanup required in Escape/Cmd+S handlers
 * - One-shot by design — consumed after first read
 * - No prop threading through 9+ intermediate components
 */

import { create } from "zustand"

interface EditFocusState {
  /** The ID of the item that should receive title focus, or null */
  pendingId: string | null

  /** Call this when creating a new item that should auto-focus its title */
  requestEditFocus: (id: string) => void

  /**
   * Call this from DetailHeader on mount — returns true (and clears)
   * if this item should receive focus. Returns false otherwise.
   */
  consumeEditFocus: (id: string) => boolean

  /**
   * True when `requestEditFocus(id)` ran within the last `withinMs`.
   *
   * requestEditFocus is only called for newly created items (see the note at
   * the top of this file), so this doubles as "was this just created?".
   * useStaleTaskDetection needs it: a brand-new task is not yet in the view's
   * live query, and without this exemption the 800ms stale timer closed it
   * again with "Task moved out of this view" before the list caught up.
   */
  wasRecentlyCreated: (id: string, withinMs: number) => boolean
}

/** id -> Date.now() when it was announced as newly created. */
const newlyCreated = new Map<string, number>()
const NEW_ID_TTL_MS = 60_000

export const useEditFocusStore = create<EditFocusState>((set, get) => ({
  pendingId: null,

  requestEditFocus: (id) => {
    const now = Date.now()
    for (const [k, t] of newlyCreated) if (now - t > NEW_ID_TTL_MS) newlyCreated.delete(k)
    newlyCreated.set(id, now)
    set({ pendingId: id })
  },

  wasRecentlyCreated: (id, withinMs) => {
    const t = newlyCreated.get(id)
    return t !== undefined && Date.now() - t < withinMs
  },

  consumeEditFocus: (id) => {
    if (get().pendingId === id) {
      set({ pendingId: null })
      return true
    }
    return false
  },
}))
